import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';

/**
 * C2-FRO-002 Fase 2 — Servicio de conectividad para validate.component +
 * SyncOrchestratorService.
 *
 * Expone `isOnline$: Observable<boolean>` con `distinctUntilChanged` para que
 * los subscribers no reciban emisiones duplicadas al alternar rápido el
 * estado (caso real en wifi inestable).
 *
 * IMPORTANTE: `navigator.onLine` es notoriamente poco confiable en algunos
 * browsers / redes — especialmente cuando la wifi conecta pero el backend
 * está inalcanzable (DNS roto, captive portal, etc.). Por eso esta clase
 * NO depende exclusivamente de navigator.onLine:
 *   - Source primario: eventos `window.online` / `window.offline`.
 *   - Heartbeat opcional (FUTURE): ping a `/health` cada 30s puede detectar
 *     conexiones zombie. Hoy no implementado para mantener el alcance
 *     limitado de Fase 2; se documenta como follow-up.
 *
 * Trade-off del approach actual: si la wifi conecta pero el backend no
 * responde, `isOnline$` emitirá `true` y el SyncOrchestrator intentará el
 * sync — el backoff exponencial maneja los 5xx/timeouts sin colapsar la UI.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService implements OnDestroy {
  private readonly _isOnline$: BehaviorSubject<boolean>;

  private readonly handleOnline = (): void => this._isOnline$.next(true);
  private readonly handleOffline = (): void => this._isOnline$.next(false);

  constructor() {
    const initial = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this._isOnline$ = new BehaviorSubject<boolean>(initial);
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  /**
   * Observable con el estado de conexión actual. Aplica `distinctUntilChanged`
   * para evitar que subscribers (e.g. SyncOrchestrator) reciban dos `true`
   * seguidos por toggles rápidos.
   */
  get isOnline$(): Observable<boolean> {
    return this._isOnline$.pipe(distinctUntilChanged());
  }

  /** Snapshot sincrónico — útil dentro de loops async donde no se puede await observable. */
  getSnapshot(): boolean {
    return this._isOnline$.getValue();
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
    this._isOnline$.complete();
  }
}
